import { ChevronDown } from "lucide-react";
import * as React from "react";
import { useState } from "react";

interface ToolCallQueryAccordionProps {
  displayData: string;
  rawData: string;
  isJson: boolean;
}

export const ToolCallQueryAccordion: React.FC<ToolCallQueryAccordionProps> = ({
  displayData,
  rawData,
  isJson
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  if (!displayData) return null;
  
  return isJson ? (
    /* Accordion for JSON query display */
    <div className="border border-border rounded overflow-hidden">
      {/* Accordion Header - Simplified Query */}
      <div
        className="bg-muted p-2 font-mono text-xs cursor-pointer hover:bg-card flex justify-between items-center"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="whitespace-nowrap overflow-hidden text-ellipsis flex-1 pr-2">
          {displayData.split("\n")[0]}
          {displayData.split("\n").length > 1 ? "..." : ""}
        </div>
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
        />
      </div>

      {/* Accordion Content - Full Raw Query */}
      {isExpanded && (
        <div className="font-mono text-xs whitespace-pre-wrap p-3 bg-popover border-t border-border">
          {/* Render JSON with syntax highlighting */}
          <pre className="text-xs whitespace-pre-wrap">
            {rawData.split("\n").map((line, i) => {
              // Apply simple syntax highlighting
              const highlightedLine = line
                // Highlight keys
                .replace(
                  /(".*?"):/g,
                  '<span class="text-yellow-400">$1</span>:',
                )
                // Highlight string values (including those at the end of a line without commas)
                .replace(
                  /: (".*?")([,}]|$)/g,
                  ': <span class="text-green-400">$1</span>$2',
                )
                // Highlight number values (including those at the end of a line without commas)
                .replace(
                  /: (-?\d+\.?\d*)([,}]|$)/g,
                  ': <span class="text-blue-400">$1</span>$2',
                )
                // Highlight boolean values (including those at the end of a line without commas)
                .replace(
                  /: (true|false)([,}]|$)/g,
                  ': <span class="text-purple-400">$1</span>$2',
                )
                // Highlight null values (including those at the end of a line without commas)
                .replace(
                  /: (null)([,}]|$)/g,
                  ': <span class="text-red-400">$1</span>$2',
                );

              return (
                <div
                  key={i}
                  dangerouslySetInnerHTML={{ __html: highlightedLine }}
                  className="leading-tight"
                />
              );
            })}
          </pre>
        </div>
      )}
    </div>
  ) : (
    /* Simple display for plain text query */
    <div className="font-mono text-xs max-h-[300px] overflow-auto whitespace-pre-wrap p-2 bg-muted rounded border border-border">
      {displayData}
    </div>
  );
};